/*    
<summary>
  This functional component "AddStoreDetails" defines the props like initial values and functions for the child form component.
</summary>
<returns>Returns JSX</returns>
*/
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { cloneDeep } from "lodash";
import { useStore } from "../../../../contexts/store-provider";
import { IUpdateStoreManagement } from "../../../../models/forms/IAddStore";
import { formatMessage } from "../../../../translations/format-message";
import { storeCategoryOptions } from "../../../../constants/options.constant";
import {
  getStoreIdFromLocalStorage,
  getUserId,
} from "../../../../helpers/localstorage-helper";
import StoreDetailsForm from "../forms/add-edit-store-details-form";
const AddStorDetails = (props: any) => {
  const { storeManagementStore, masterManagementStore } = useStore();
  const {
    GetStoreManagementByIdService,
    resetAddUpdateStoreManagementState,
    resetStoreManagementDetail,
    storeState,
    storeDetail,
    addUpdateStoreManagementState,
    AddStoreManagementService,
    UpdateStoreManagementService,
    inProgress,
    resetPrefectureByZipCode
  } = storeManagementStore;
  const { getAllBusinessTypes, GetAllBusinessTypeService } =
    masterManagementStore;
  const initialValues: any = storeDetail;

  const storeId = getStoreIdFromLocalStorage();
  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllBusinessTypeService();
    if (storeId > 0) GetStoreManagementByIdService(storeId);
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStoreManagementState.success) {
      toast.success(formatMessage("updated_success"));
      resetAddUpdateStoreManagementState();
      resetStoreManagementDetail();
      GetStoreManagementByIdService(storeId);
      resetPrefectureByZipCode();
    }
  }, [addUpdateStoreManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "storeDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (storeState.error) {
      toast.error(formatMessage(storeState.error));
      resetStoreManagementDetail();
    }
  }, [storeState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStoreManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStoreManagementState.error) {
      toast.error(formatMessage(addUpdateStoreManagementState.error));
      resetAddUpdateStoreManagementState();
    }
  }, [addUpdateStoreManagementState.error]);

  /**
   * This function adds the store when "Id < 0" and updates the Existing store when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: IUpdateStoreManagement) => {
    let tempValues = cloneDeep(values);
    tempValues.StoreName = values.StoreName.trim();
    UpdateStoreManagementService({
      ...tempValues,
      Id: storeId,
      IsPasswordUpdated: tempValues.Password ? true : false,
    });
  };

  return (
    <React.Fragment>
      <StoreDetailsForm
        initialValues={initialValues}
        id={storeId}
        businessTypeOptions={getAllBusinessTypes}
        storeCategoryOptions={storeCategoryOptions}
        submitHandler={onSubmitForm}
        getStoreManagementLoading={storeState.inProgress || inProgress}
        addUpdateStoreManagementLoading={
          addUpdateStoreManagementState.inProgress
        }
      />
    </React.Fragment>
  );
};

export default observer(AddStorDetails);
